const MS_MINUTE = 60 * 1000;
const MS_HOUR = 60 * MS_MINUTE;

const isParseableDate = (date) =>
  !!date && !isNaN(new Date(date).getTime());

const getMostRecentParseableDate = (...dates) => {
  const valid = dates.filter(isParseableDate);
  if (!valid.length) return null;

  return valid.reduce((latest, current) =>
    new Date(current) > new Date(latest) ? current : latest
  );
};

/**
 * Liveblogs del body con value.active === true.
 * @param {Array} body
 * @returns {Array}
 */
export const getActiveLiveblogs = (body = []) =>
  (body || []).filter(
    (item) => item?.type === "liveblog" && item?.value?.active === true
  );

/**
 * Fecha de un liveblog: último post (dateParagraph) o modifiedAt/modificatedAt.
 * @param {Object} liveblog
 * @returns {string|null}
 */
export const getLiveblogUpdateDate = (liveblog) => {
  const value = liveblog?.value || {};
  const posts = (value.body || []).filter((el) =>
    isParseableDate(el?.dateParagraph)
  );

  let latestPost = null;
  if (posts.length > 0) {
    latestPost = posts.reduce((latest, current) =>
      new Date(current.dateParagraph) > new Date(latest.dateParagraph)
        ? current
        : latest
    ).dateParagraph;
  }

  const modifiedAt = value.modificatedAt || value.modifiedAt;

  return getMostRecentParseableDate(latestPost, modifiedAt);
};

/**
 * Fecha de actualización del último liveblog activo del body.
 * Si se pasa articleModificatedAt, usa la más reciente entre liveblog y nota.
 * Si no hay liveblog activo, retorna null (no mostrar "Actualizado hace…").
 * @param {Array} body
 * @param {string|Date} [articleModificatedAt]
 * @returns {string|Date|null}
 */
export const getActiveLiveblogUpdateDate = (body, articleModificatedAt) => {
  const activeLiveblogs = getActiveLiveblogs(body);
  if (!activeLiveblogs.length) return null;

  const ranked = activeLiveblogs
    .map((liveblog) => {
      const updateDate = getLiveblogUpdateDate(liveblog);
      const value = liveblog?.value || {};
      const rankingDate =
        value.modificatedAt || value.modifiedAt || updateDate;

      return { updateDate, rankingDate };
    })
    .filter((item) => item.updateDate);

  const liveblogDate = ranked.length
    ? ranked.sort(
        (a, b) =>
          new Date(b.rankingDate).getTime() - new Date(a.rankingDate).getTime()
      )[0].updateDate
    : null;

  return getMostRecentParseableDate(liveblogDate, articleModificatedAt);
};

/**
 * Formato relativo para liveblog:
 * - menos de 1h: "7 min." → en UI: "Actualizado hace 7 min."
 * - 1h o más: "1 hora" → en UI: "Actualizado hace 1 hora"
 * @param {string|Date} date
 * @param {Date} [now]
 * @returns {string|null}
 */
export const formatRelativeLiveblog = (date, now = new Date()) => {
  const then = new Date(date);
  if (isNaN(then.getTime()) || isNaN(now.getTime())) return null;

  const remaining = Math.max(0, now.getTime() - then.getTime());

  if (remaining >= MS_HOUR) {
    return "más de 1 hora";
  }

  const minutes = Math.floor(remaining / MS_MINUTE);
  return `${minutes} min.`;
};
